import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Col, Fade, Modal, Spinner, Table } from 'react-bootstrap';

import AddButton from '../components/AddButton';
import ButtonType from '../components/ButtonType';
import CollapseField from '../components/CollapseField';
import Delayed from '../components/Delayed';
import LoadingWrapper from '../components/LoadingWrapper';
import ProjectDropdown from '../components/ProjectDropdown';
import SelectField from '../components/SelectField';
import { infoToast } from '../components/Toasty';

import { RoleGroup } from '../enums/RoleGroup';

import DashboardService from '../services/DashboardService';
import DataService from '../services/DataService';
import FormApproverService from '../services/FormApproverService';
import RoleGroupService from '../services/RoleGroupService';
import TestService from '../services/TestService';

import IProjectList from '../types/Dashboard/ProjectList';
import EmployeeDetail from '../types/Employee/EmployeeDetail';
import EmployeeInfo from '../types/Employee/EmployeeInfo';
import FormApproverGroupDetail from '../types/FormApprover/FormApproverGroupDetail';
import RoleGroupList from '../types/RoleGroup/RoleGroupList';

import * as JobCodes from '../constants/JobCodes';

type Props = {
    currentUser: EmployeeInfo,
    isMocked: boolean,
    cookieName: string,
}

// Constant variables
const SAVESUCCESS = "SaveSuccess";
const SAVEERROR = "SaveError";
const FORM_APPROVER_CODES_A = [JobCodes.SITE_MANAGER, JobCodes.PROJECT_CONSTRUCTION_COORDINATOR, JobCodes.PROJECT_ENGINEER, JobCodes.FIELD_ENGINEER, JobCodes.ASSISTANT_STRUCTURES_SUPERINTENDENT, JobCodes.ASSISTANT_GRADING_SUPERINTENDENT, JobCodes.ASSISTANT_ELECTRICAL_SUPERINTENDENT, JobCodes.ASSISTANT_INSTALLATION_SUPERINTENDENT, JobCodes.GRADING_SUPERINTENDENT, JobCodes.INSTALLATION_SUPERINTENDENT, JobCodes.STRUCTURES_SUPERINTENDENT, JobCodes.ELECTRICAL_SUPERINTENDENT, JobCodes.COMMISSIONING_SUPERINTENDENT];
const FORM_APPROVER_CODES_B = [JobCodes.SITE_MANAGER, JobCodes.PROJECT_CONSTRUCTION_COORDINATOR, JobCodes.PROJECT_ENGINEER, JobCodes.ASSISTANT_ELECTRICAL_SUPERINTENDENT, JobCodes.ELECTRICAL_SUPERINTENDENT, JobCodes.FIELD_ENGINEER];

const GROUP_TO_JOBCODE = {
    "Civil FE": FORM_APPROVER_CODES_A,
    "Civil Superintendent": FORM_APPROVER_CODES_A,
    "Collections FE": FORM_APPROVER_CODES_B,
    "Collections Superintendent": FORM_APPROVER_CODES_B,
    "Commissioning Foreperson": [...FORM_APPROVER_CODES_B, JobCodes.COMMISSIONING_SUPERINTENDENT],
    "Electrical BESS FE": FORM_APPROVER_CODES_B,
    "Electrical BESS Superintendent": FORM_APPROVER_CODES_B,
    "Electrical FE": FORM_APPROVER_CODES_B,
    "Electrical Superintendent": FORM_APPROVER_CODES_B,
    "Foundations BESS FE": FORM_APPROVER_CODES_A,
    "Foundations BESS Superintendent": FORM_APPROVER_CODES_A,
    "Foundations FE": FORM_APPROVER_CODES_A,
    "Foundations Superintendent": FORM_APPROVER_CODES_A,
    "Installation FE": FORM_APPROVER_CODES_A,
    "Installation Superintendent": FORM_APPROVER_CODES_A,
    "Overhead Collections FE": FORM_APPROVER_CODES_B,
    "Overhead Collections Superintendent": FORM_APPROVER_CODES_B,
    "Repower FE": FORM_APPROVER_CODES_A,
    "Repower Superintendent": FORM_APPROVER_CODES_A,
    "Substation FE": FORM_APPROVER_CODES_B,
    "Substation Superintendent": FORM_APPROVER_CODES_B,
    "Switchyard FE": FORM_APPROVER_CODES_B,
    "Switchyard Superintendent": FORM_APPROVER_CODES_B,
    "Transmission Line FE": FORM_APPROVER_CODES_B,
    "Transmission Line Superintendent": FORM_APPROVER_CODES_B,
}

const defaultUserToManualAdd: EmployeeDetail = {
    employeeJobCode: "",
    employeeLocation: "",
    employeeID: "",
    employeeName: "",
    employeeEmail: "",
    manualAdd: false
};

const QualityFormApproversPage: React.FC<Props> = ({ currentUser, isMocked, cookieName }) => {
    const saveStatusTimeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    const [projectLoading, setProjectLoading] = useState(true);
    const [selectedProject, setSelectedProject] = useState("");
    const [projectList, setProjectList] = useState<IProjectList>({
        projects: [],
        defaultProjectID: ""
    });
    const [approversLoading, setApproversLoading] = useState(false);
    const [approverGroups, setApproverGroups] = useState<FormApproverGroupDetail[]>([]);
    const [manualAddUpdates, setManualAddUpdates] = useState<RoleGroupList[]>([]);
    const [openModal, setOpenModal] = useState(false);
    const [manualAddGroupName, setManualAddGroupName] = useState("");
    const [userToManualAdd, setUserToManualAdd] = useState<EmployeeDetail>(defaultUserToManualAdd);
    const [userAlreadyAdded, setUserAlreadyAdded] = useState(false);
    const [searchFieldOptions, setSearchFieldOptions] = useState<EmployeeDetail[]>([]);
    const [canEdit, setCanEdit] = useState(false);
    const [changesMade, setChangesMade] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [saveStatus, setSaveStatus] = useState("");
    const [showSaveStatus, setShowSaveStatus] = useState(false);

    /**
     * Gets form approver data
     * @param project selected project's companyID
     */
    const getFormApprovers = useCallback(async (project: string) => {
        try {
            const qualityFormApproversPromise = Promise.all([
                FormApproverService.getFormApproversGroup(project),
                RoleGroupService.getAllGroupMembers(project, currentUser.email),
                FormApproverService.getFormApprovers(project),
            ]);
            const [
                { data: ftcGroupDetails },
                { data: manualAddGroupDetails },
                { data: legacyGroupDetails },
            ] = await qualityFormApproversPromise;

            const groupsToFilter = ['DOT Coordinators', 'None'];
            const filteredManualAddGroup = manualAddGroupDetails
                .filter((e) => (!groupsToFilter.includes(e.roleGroupName)))
                .filter((e) => (e.roleGroupMembers.length !== 0));

            // Create the new list with manually added users
            const newApproverGroups: FormApproverGroupDetail[] = filteredManualAddGroup.map((group) => ({
                groupName: group.roleGroupName,
                groupEditable: true,
                users: group.roleGroupMembers.map((user) => ({ ...user, employeeEmail: 'N/A', manualAdd: true, employeeLocation: 'unknown', employeeJobCode: '' }))
            }));

            // Preppend approvers list with First to Claim Group approvers
            ftcGroupDetails.forEach(({ groupName, users }) => {
                const groupIndex = newApproverGroups.findIndex((g) => g.groupName === groupName);
                if (groupIndex === -1) {
                    newApproverGroups.push({
                        groupName,
                        groupEditable: true,
                        users: users.map((user) => ({ ...user, manualAdd: false }))
                    });
                } else {
                    users.forEach((user) => {
                        if (newApproverGroups[groupIndex].users.findIndex((u) => u.employeeID === user.employeeID) === -1) {
                            newApproverGroups[groupIndex].users.unshift({ ...user, manualAdd: false });
                        }
                    });
                }
            });

            // Append any Systems Workflows approvers that may be hold overs from legacy access
            legacyGroupDetails.forEach(({title, employeeID, employeeName, email: employeeEmail}) => {
                const groupName = title.trim() === "Commissioning Lead" ? "Commissioning Foreperson" : title.trim();
                // Need to remove the '/FCC' from the legacy names below to prevent duplicate groups.
                if (newApproverGroups.findIndex((g) => g.groupName === groupName.replace('/FCC', '')) === -1) {
                    newApproverGroups.push({
                        groupName,
                        groupEditable: false,
                        users: [{employeeID, employeeName, employeeEmail, employeeLocation: '', employeeJobCode: '', manualAdd: false}]
                    });
                }
            });

            // setup mechanism for tracking manual add actions
            const newManualAddUpdates: RoleGroupList[] = filteredManualAddGroup.map((group) => ({
                addUpdates: [],
                removeUpdates: [],
                updatedGroup: [],
                companyID: project,
                groupName: RoleGroup[group.roleGroupName as keyof typeof RoleGroup],
                modifiedUser: currentUser.email,
            }));

            if (!mountedRef.current) return;
            setApproverGroups(newApproverGroups);
            setManualAddUpdates(newManualAddUpdates);

        } catch (error) {
            if (!mountedRef.current) return;
            console.log('An error occurred while getting the manual add data', error);
        }

        if (mountedRef.current) setApproversLoading(false);
    }, [currentUser.email]);

    /**
     * Gets projects for the dropdown and sets canEdit permissions
     */
    const getUserProjects = useCallback(async () => {
        const userEmail = currentUser.email.toLowerCase();

        // Get projects for dropdown
        try {
            const response = await DashboardService.getUserProjects(userEmail);
            if (!mountedRef.current) return;
            setProjectLoading(false);
            setProjectList(response.data);
            if (response.data.defaultProjectID) {
                setSelectedProject(prev => prev || response.data.defaultProjectID || "");
            }
        } catch (e) {
            if (!mountedRef.current) return;
            setProjectLoading(false);
        }

        // Get logged in user's test groups
        let testGroups: any[] = [];
        try {
            const response = await TestService.getGroup(userEmail);
            if (!mountedRef.current) return;
            if (response.data) {
                testGroups = response.data.map(x => x.group);
            }
        } catch (e) {
            if (!mountedRef.current) return;
        }

        // Get emails of users who can edit the page (BFM600, BFM601, BFM602, BFM603)
        let canEditEmails: any[] = [];
        const canEditQuery = {
            query: [{
                column: 10,
                values: [
                    JobCodes.QUALITY_SUPERINTENDENT,
                    JobCodes.QUALITY_SUPERINTENDENT_II,
                    JobCodes.PROJECT_ENGINEER,
                    JobCodes.PROJECT_CONSTRUCTION_COORDINATOR
                ]
            }]
        };
        try {
            const response = await DataService.searchDataSourceDataWithQuery("BOOMI_EMPLOYEES_ENTERPRISE", 5, canEditQuery);
            if (!mountedRef.current) return;
            if (response.data) {
                canEditEmails = response.data.map((email: string) => email.toLowerCase());
            }
        } catch (e) {
            if (!mountedRef.current) return;
        }

        // Set canEdit permissions
        if (canEditEmails.includes(userEmail) || testGroups.includes("Quality_Supt")) {
            setCanEdit(true);
        }
    }, [currentUser.email]);

    // Component mount effect - get user projects
    useEffect(() => {
        getUserProjects();
    }, [getUserProjects]);

    // Cleanup effect for timeout
    useEffect(() => {
        return () => {
            if (saveStatusTimeoutId.current) {
                clearTimeout(saveStatusTimeoutId.current);
            }
        };
    }, []);

    /**
     * Saves the updates to the database tables
     */
    const saveUpdates = async () => {
        // if we are logged in as a different user, do not allow the save actions to happen
        if (isMocked) {
            infoToast('You have clicked on the "Save" button.  This has been disabled because you are currently not yourself.');
            setOpenModal(false);
            return;
        }
        setSaveLoading(true);
        setChangesMade(false);
        setShowSaveStatus(true);

        let responseError = false;

        for (const group of manualAddUpdates) {
            if (group.addUpdates.length || group.removeUpdates.length) {
                try {
                    const response = await RoleGroupService.updateGroupMembers(group);
                    if (!(response.status >= 100 && response.status <= 399)) {
                        responseError = true;
                    }
                } catch (e) {
                    responseError = true;
                }
            }
        }

        if (responseError) {
            setSaveStatus(SAVEERROR);
            setSaveLoading(false);
        } else {
            setSaveStatus(SAVESUCCESS);
            setSaveLoading(false);
        }

        // Set showSaveStatus back to false after 10 seconds
        if (saveStatusTimeoutId.current) {
            clearTimeout(saveStatusTimeoutId.current);
        }
        saveStatusTimeoutId.current = setTimeout(() => {
            setShowSaveStatus(false);
        }, 10000);
    };

    /**
     * Checks if any updates were made and sets the changesMade state
     */
    const checkChangesMade = (updates: RoleGroupList[]) => {
        let newChangesMade = false;

        updates.forEach((group) => {
            if (group.addUpdates.length || group.removeUpdates.length) {
                newChangesMade = true;
            }
        });

        setChangesMade(newChangesMade);
    };

    /**
     * Adds a user to a group
     */
    const manualAdd = () => {
        // Update display groups
        const newApproverGroups = approverGroups.slice();
        const approverGroupIndex = newApproverGroups.findIndex(x => x.groupName === manualAddGroupName);
        newApproverGroups[approverGroupIndex].users.push(userToManualAdd);
        
        // Update RoleGroup DB table
        const newManualAddUpdates = manualAddUpdates.slice();
        let updateGroupIndex = newManualAddUpdates.findIndex(x => x.groupName === RoleGroup[manualAddGroupName as keyof typeof RoleGroup]);
        if (updateGroupIndex === -1) {
            // if the group isn't in the list add a group
            newManualAddUpdates.push({
                addUpdates: [],
                removeUpdates: [],
                updatedGroup: [],
                companyID: selectedProject,
                groupName: RoleGroup[manualAddGroupName as keyof typeof RoleGroup],
                modifiedUser: currentUser.email,
            });
            updateGroupIndex = newManualAddUpdates.length - 1;
        }

        newManualAddUpdates[updateGroupIndex].addUpdates.push({
            employeeID: userToManualAdd.employeeID,
            employeeName: userToManualAdd.employeeName
        });

        // clear user from removal table
        const newRemoveUpdates = newManualAddUpdates[updateGroupIndex].removeUpdates
          .filter(x => x.employeeID !== userToManualAdd.employeeID);
        
        newManualAddUpdates[updateGroupIndex].removeUpdates = newRemoveUpdates;

        setApproverGroups(newApproverGroups);
        setManualAddUpdates(newManualAddUpdates);
        setUserToManualAdd(defaultUserToManualAdd);
        setChangesMade(true);
        setOpenModal(false);
    };

    /**
     * Removes a user from a group
     * @param groupName name of the group
     * @param userToRemove user to remove from the group
     */
    const manualRemove = (groupName: string, userToRemove: EmployeeDetail) => {
        // Update display groups
        const newApproverGroups = approverGroups.slice();
        const approverGroupIndex = newApproverGroups.findIndex(x => x.groupName === groupName);
        const approverUserIndex = newApproverGroups[approverGroupIndex].users.findIndex(x => x.employeeID === userToRemove.employeeID);
        newApproverGroups[approverGroupIndex].users.splice(approverUserIndex, 1);

        // Update manualAddUpdates state
        const newManualAddUpdates = manualAddUpdates.slice();
        const updateGroupIndex = newManualAddUpdates.findIndex(x => x.groupName === RoleGroup[groupName as keyof typeof RoleGroup]);

        // Update addUpdates list (if applicable), otherwise update removeUpdates list
        if (newManualAddUpdates[updateGroupIndex].addUpdates.findIndex(x => x.employeeID === userToRemove.employeeID) !== -1) {
            const newAddUpdates = newManualAddUpdates[updateGroupIndex].addUpdates
            .filter(x => x.employeeID !== userToRemove.employeeID);

            newManualAddUpdates[updateGroupIndex].addUpdates = newAddUpdates;
        }
        else {
            newManualAddUpdates[updateGroupIndex].removeUpdates.push({
                employeeID: userToRemove.employeeID,
                employeeName: userToRemove.employeeName
            });
        }

        setApproverGroups(newApproverGroups);
        setManualAddUpdates(newManualAddUpdates);
        checkChangesMade(newManualAddUpdates);
    };

    /**
     * Sets the search field options to be users based on job codes
     * @param jobCodes list of job codes
     */
    const setSearchFieldOptionsFromJobCodes = async (jobCodes: string[]) => {
        const query = { query: [{ column: 10, values: jobCodes }] };

        // we want to use the enterprise data if we have it otherwise default ot the legacy data
        const headers = (await DataService.getAllHeaders(process.env.REACT_APP_PRIMARY_FOF_ENVIRONMENT!)).data;

        const targetDS = !!headers.dataSources.find((x) => x.externalId === 'BOOMI_EMPLOYEES_ENTERPRISE') ? 'BOOMI_EMPLOYEES_ENTERPRISE' : 'BOOMI_EMPLOYEES';

        const [
            employeeIdsResponse, firstNamesResponse, lastNamesResponse,
        ] = await Promise.all([
            DataService.searchDataSourceDataWithQuery(targetDS, 0, query),
            DataService.searchDataSourceDataWithQuery(targetDS, 8, query),
            DataService.searchDataSourceDataWithQuery(targetDS, 9, query),
        ]);

        const employeeIds = employeeIdsResponse.data;
        const firstNames = firstNamesResponse.data;
        const lastNames = lastNamesResponse.data;

        // Format employee data received from query
        const employeeData = employeeIds.map((id: string, index: number) => ({
                employeeID: id,
                employeeName: firstNames[index] + " " + lastNames[index],
                employeeEmail: "N/A",
                manualAdd: true
            })
        );
        
        setSearchFieldOptions(employeeData);
    };

    /**
     * Handles selecting a new project in the dropdown
     * @param project selected project's companyID
     */
    const handleProjectChange = useCallback((project: string | undefined) => {
        // handle if project is undefined
        if(!project) return;

        setSelectedProject(project);
        setApproversLoading(true);
        setChangesMade(false);

        getFormApprovers(project);
    }, [getFormApprovers]);

    /**
     * Handles selecting a new value in the search field
     * @param event selected value from search field
     */
    const handleSearchFieldChange = async (event: any) => {
        // Reset userAlreadyAdded to false
        setUserAlreadyAdded(false);

        if (event.target.value) {
            const employeeIndex = searchFieldOptions.findIndex(x => x.employeeID === event.target.value);
            const employee = searchFieldOptions[employeeIndex];

            setUserToManualAdd(employee);
            
            // Check if person is already in the group, set userAlreadyAdded to true if true
            const groupIndex = approverGroups.findIndex(x => x.groupName === manualAddGroupName);
            approverGroups[groupIndex].users.forEach((member) => {
                if (member.employeeID === employee.employeeID) {
                    setUserAlreadyAdded(true);
                }
            });
        }
        else {
            setUserToManualAdd(defaultUserToManualAdd);
        }
    };

    /**
     * Opens the manual add modal
     * @param groupName name of group
     */
    const onOpenManualAddModal = (groupName: string) => {
        setSearchFieldOptionsFromJobCodes(GROUP_TO_JOBCODE[groupName as keyof typeof GROUP_TO_JOBCODE]);
        
        setOpenModal(true);
        setManualAddGroupName(groupName);
    };

    /**
     * Closes the modal
     */
    const onCloseModal = () => {
        setOpenModal(false);
        setManualAddGroupName("");
        setUserAlreadyAdded(false);
        setUserToManualAdd(defaultUserToManualAdd);
    };

    const FormApprovers = approversLoading ?
        <tr>
            <td colSpan={100}>
                <Delayed>
                    <div className="DivPadded DivCenter"><Spinner animation="border" role="status" variant="primary" /></div>
                </Delayed>
            </td>
        </tr> :
        approverGroups.length > 0 ?
            approverGroups.slice().sort((a, b) => a.groupName > b.groupName ? 1 : a.groupName < b.groupName ? -1 : 0).map((group) => 
                <tr key={group.groupName}>
                    <td>
                        <CollapseField title={group.groupName} hintText={group.users.length + (group.users.length === 1 ? " group member" : " group members")} buttonStyle="collapse-button">
                            <div>
                                <Table>
                                    <tbody>
                                        {group.users.map((user) => 
                                            <tr key={group.groupName + user.employeeID}>
                                                <td style={{height: 55}}>
                                                    <div className='row align-middle' >
                                                        <div className='col-11 pt-2'>
                                                            {user.employeeName}<b>{user.manualAdd ? " *" : ""}</b>
                                                        </div>
                                                        <div className='pt-1 col-1 text-center'>
                                                            {canEdit && user.manualAdd ? <ButtonType dataName="removeMember" buttonText="x" color="red" size="sm" eventHandler={() => manualRemove(group.groupName, user)} /> : null}
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                        {canEdit && group.groupEditable ? 
                                        <tr>
                                            <td style={{borderBottom: 'none'}} >
                                                <Col style={{display:'flex', justifyContent:'right'}}>
                                                    <AddButton dataName="manualAdd" title="Manual Add" eventHandler={() => onOpenManualAddModal(group.groupName)} />
                                                </Col>
                                            </td>
                                        </tr>
                                        : null}
                                    </tbody>
                                </Table>
                            </div>
                        </CollapseField>
                    </td>
                </tr>
            ) :
            <tr>
                <td colSpan={100}>
                    <b>No Form Approver Groups Found</b>
                </td>
            </tr>;

    return(
        <div className='w-75'>
        <LoadingWrapper showLoading={projectLoading} loadingMessage="Getting Quality Form Approvers..." windSpinner windSpinnerScale={3}>
            <Modal show={openModal} onHide={onCloseModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>New {manualAddGroupName}:</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className='h6'>
                        The PCC/PE or Quality Superintendent <b>must</b> call the trade General Superintendent to get verbal confirmation followed by an email that the person can be added to this group.
                    </div>
                    <div>
                        <SelectField 
                            dataName="memberSelect" 
                            title="Select an Employee" 
                            options={searchFieldOptions
                                .slice().sort((a, b) => a.employeeName > b.employeeName ? 1 : -1)
                                .map(({employeeName, employeeID}) => { return { label: employeeName, value: employeeID}})} 
                            value={userToManualAdd.employeeID}
                            onchange={handleSearchFieldChange}
                         />
                        <br></br>
                        {userAlreadyAdded ? 
                            <div>
                                <b>This person is already in the group.</b>
                                <br />
                            </div>
                        : 
                            <div>
                                <small><i>Note: The manually added individual also needs to be added to the project in Blattner Mobile (if not already added). </i></small>
                            </div>
                        }
                        <br />
                        <div className="DivCenter">
                            <ButtonType
                                eventHandler={() => manualAdd()}
                                dataName="addMember"
                                buttonText="Add"
                                color="blue"
                                readOnly={userToManualAdd.employeeID === "" || userAlreadyAdded}
                            />
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
            <ProjectDropdown
                projects={projectList.projects}
                cookieName={cookieName}
                onProjectChange={handleProjectChange}
            />

            <h1 className="header mt-5 mb-4 text-center">Quality Form Approver Groups</h1>

            {selectedProject ?
                <div>
                    <div id="RightAlignedFloatDiv">
                    {canEdit ?
                        changesMade ?
                            <ButtonType dataName="save" color="blue" buttonText="Save" type="submit" eventHandler={() => saveUpdates()} />
                        : saveLoading ? 
                            <div className="DivPadded DivCenter"><Spinner animation="border" role="status" variant="primary" /></div>
                        : saveStatus === SAVESUCCESS ?
                            <div className="DivPadded DivCenter">
                                <Fade in={showSaveStatus} >
                                    <b>Save Successful ✔</b>
                                </Fade>
                            </div>
                        : saveStatus === SAVEERROR ?
                            <div className="DivPadded DivCenter">
                                <b>An Error Occurred ⚠️</b><p><small><i>Please Refresh and Try Again</i></small></p>
                            </div>
                        : null
                    : null}
                    </div>
                    <div className="flex-grow-1 flex-wrap DivLeft">
                        <p>Process:</p>
                        <ol>
                            <li>Use the Add or Update User form to add someone to the Project's Blattner Mobile.</li>
                            <li>If they are not in the expected group (below) the next day, then work with the PCC/PE or Quality Superintendent.</li>
                            <ol type="a">
                                <li>The PCC/PE or Quality Superintendent <b>must</b> call the trade General Superintendent to get verbal confirmation followed by an email that the person can be in the group.</li>
                            </ol>
                        </ol>
                        <p><i>Note: An (<b>*</b>) after a name indicates the PCC/PE or Quality Superintendent manually added the person to the group. Manually added individuals are put into their Blattner Mobile groups overnight.</i></p>
                    </div>
                    <Table>
                        <tbody>
                            {FormApprovers}
                        </tbody>
                    </Table>
                </div>
            : null}
            <br />
            <br />
            <br />
            <br />
        </LoadingWrapper>
        </div>
    );
};

export default QualityFormApproversPage;